import { float } from 'html2canvas/dist/types/css/property-descriptors/float';
import { ProjectData } from './entities/project-data.entity';

export class Utils {

	// Es la cadena que tiene almacenada el campo renderer de un proyecto e indica el tipo.
	public static SCHEME2 = 'canvas2d';
	public static SATELITE = 'gmaps';

	// Utilizado como key para el local storage.
	public static CURRENT_PROJECT = 'currentProject';
	public static PREVIOUS_CURRENT_PROJECT = 'previousCurrentProject';

	public static ESTADO_BORRADOR = 0;
	public static ESTADO_EN_REVISION = 1;
	public static ESTADO_OFERTA = 2;
	public static ESTADO_PEDIDO = 3; //Aceptado.
	public static ESTADO_ACABADO = 4;
	public static ESTADO_CANCELADO = 5;

	// ##########################################################################################################
	// ################################################################################################## MÉTODOS

	// Formatea un string con decimales en inglés a decimales en español.
	// Si no se indica la cantidad de decimales asume 2 por defecto.
	public static formatDecimal(value: string, decimals = 2) {
		// Si value está indefinido devuelve una cadena de valor cero y decimales a cero.
		if (!value) return '0,0000000'.substr(0, 2 + decimals);

		// Convertir el string a float. Eliminar la coma de los millares.
		let f = parseFloat(value.replace(',', ''));

		// Si el número no es lógico retornar ceros.
		if (f === NaN) return '0,0000000'.substr(0, 2 + decimals);

		// Convertir a string el número corregido teniendo en cuenta los decimales.
		let aux = f.toFixed(decimals);

		// Quitar las comas y sustituir los puntos por comas.
		aux = aux.replace(',', '').replace('.', ',');

		// Ajustes de millares.
		let coma = aux.indexOf(',');
		if (coma === -1) coma = aux.length;
		if (coma > 3) {
			let dec = aux.substr(0, coma);
			aux = aux.substr(coma);
			while (coma > 0) {
				aux = dec.slice(-3) + aux;
				coma -= 3;
				if (coma > 0) { 
					aux = '.' + aux; 
				}
				dec = dec.substr(0, coma);
			}
		}

		return aux;
	}

	// Distancia entre dos puntos 2D.
	public static magnitud(x: number, y: number, x1: number, y1: number) {
		return Math.sqrt(Math.pow(x - x1, 2) + Math.pow(y - y1, 2));
	}

	public static magnitud3D(x: number, y: number, z: number, x1: number, y1: number, z1: number) {
		return Math.sqrt(Math.pow(x - x1, 2) + Math.pow(y - y1, 2) + Math.pow(z - z1, 2));
	}

	constructor() { }

	// Ajuste de decimales, en javascript es habitual que genera números como 2.000000000001
	// Para evitar esto, se utiliza este delimitador, donde se le indica la cantidad de decimales que debe respetar (por defecto 2).
	// NO REDONDEA.
	public static ajustDecimal(value: number, decimal: number = 2) {
		if (!value || !decimal) return -1; // Error.
		return parseFloat(value.toFixed(decimal));
	}

	// No permitir más de 5 decimales.
	public static ajusteDecimal5(valor: number): number { return Math.round(valor * 100000) / 100000; }

	// Da el angulo creado con tres puntos conectados, x,y es el punto central; x1,y1,x2,y2 son los puntos extremos.
	public static anguloEntreDos(x: number, y: number, z: number, x1: number, y1: number, z1: number, x2: number, y2: number, z2: number, digitos = 2) {
		x1 -= x; x2 -= x;
		y1 -= y; y2 -= y;
		z1 -= z; z2 -= z;
		let up = (x1 * x2) + (y1 * y2) + (z1 * z2);
		let down = Math.sqrt((x1 * x1) + (y1 * y1) + (z1 * z1)) * Math.sqrt((x2 * x2) + (y2 * y2) + (z2 * z2));
		let r = (up / down);
		return Math.acos(r) * 180 / Math.PI;
	}

	public static anguloEntreVertices(x1: number, y1: number, z1: number, x2: number, y2: number, z2: number, digitos = 2) {
		let up = (x1 * x2) + (y1 * y2) + (z1 * z2);
		let down = Math.abs(Math.sqrt((x1 * x1) + (y1 * y1) + (z1 * z1))) * Math.abs(Math.sqrt((x2 * x2) + (y2 * y2) + (z2 * z2)));
		let r = (up / down);
		return {
			acos: Math.acos(r) * 180 / Math.PI,
			asin: Math.asin(r) * 180 / Math.PI,
			sin: Math.sin(r) * 180 / Math.PI,
			cos: Math.cos(r) * 180 / Math.PI
		};
	}

	// Elimina campos añadadidos por el SVG.
	// Útil para limpiar el proyecto antes de salvar.
	public static clearSVGinfoProject(project: ProjectData) {
		let p = {
			perimetro: [{
				dotsBreak: 0,
				coordenadas: [],
				puertas: [],
				css: 0,
				id: 0
			}],
		};
		Object.assign(p, project);
		for (let pe of p.perimetro) {
			delete pe.dotsBreak;
			delete pe.css;
			delete pe.id;
			for (let c of pe.coordenadas) {
				delete c.vx;
				delete c.vy;
				delete c.id;
			}
			for (let pu of pe.puertas) {
				delete pu.idSVG;
			}
		}
		return p;
	}

	// ###################################################################### DOM
	public static DOM_WebHeader_show(show: boolean) {
		const webHeader = window.document.getElementById('DOMWebHeader');
		if (webHeader) {
			webHeader.style.display = (show) ? '' : 'none';
		}
	}

	public static DOMWebHeader_setTitleProject(title: string) {
		const dom = window.document.getElementById('DOMWebHeader_titleProject');
		if (dom) {
			dom.innerText = title;
		}
	}

	public static DOMWebHeader_getTitleProject(): string {
		const dom = window.document.getElementById('DOMWebHeader_titleProject');
		if (dom) { return dom.innerText; }
		return '';
	}

	public static DOMWebHeader_setStatusProject(status: string) {
		const dom = window.document.getElementById('DOMWebHeader_statusProject');
		if (dom) {
			dom.innerText = status;
		}
	}

	// ###################################################################### Historial de los últimos pryectos abiertos.
	// Si devuelve null indica que no hace falta guardar (ejemplo se ha pulsado el primer item del historial).
	public static HistorySetProject(id: string, title: string, lista?: { id: string, title: string }[]): { id: string, title: string }[] {
		if (lista)  {
			if (lista.length === 0) return [{ id: id, title: title }];
			// Si es el primer proyecto del historial no hace falta actualizar.
			if (lista[0].id === id) return null;
			// Si ya está en la lista, colocarlo en el primer puesto.
			let item = lista.find((i) => i.id === id);
			if (item) {
				// Ya está en el listado, situarlo al principio.
				return [item, ...lista.filter((i) => i.id !== id)];
			}
			else {
				// Se añade al principio y se limita a 10 elementos.
				return [{ id: id, title: title }, ...lista].slice(0, 10);
			}
		}
		else return [{ id: id, title: title }];
	}
}
export const utils = new Utils();
